/**
 * API クライアントクラス
 * - リトライロジック
 * - リクエストキャンセル
 * - 認証トークン管理
 * - エラーハンドリング
 */

import {
  handleApiResponse,
  logError,
  logApiRequest,
  logApiResponse,
  logPerformance
} from '../../utils/errorHandler';
import { measureApiCall } from '../../utils/webVitals';
import type { ConvertibleValue } from '../../types/api';

// API設定インターフェース
interface ApiConfig {
  baseURL: string;
  timeout?: number;
  retryCount?: number;
  retryDelay?: number;
}

// リクエストオプション
interface RequestOptions extends RequestInit {
  retry?: boolean;
  retryCount?: number;
  signal?: AbortSignal;
  timeout?: number;
  requestId?: string; // 外部から指定可能なリクエストID
}

// リトライ可能なエラーコード
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

// エクスポネンシャルバックオフの計算
const calculateBackoffDelay = (attempt: number, baseDelay: number): number => {
  return Math.min(baseDelay * Math.pow(2, attempt), 30000); // 最大30秒
};

// camelCase to snake_case conversion
const toSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

// snake_case to camelCase conversion
const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

// APIリクエストボディのセンシティブ情報をマスキング
const sanitizeRequestBody = (body: any, visited?: WeakSet<object>): any => {
  if (!body) return body;

  // FormDataやBlobの場合はマスキング不要
  if (body instanceof FormData || body instanceof Blob) {
    return '[FormData/Blob]';
  }

  // 文字列の場合はJSONとして解析を試みる
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      return JSON.stringify(sanitizeRequestBody(parsed, visited));
    } catch {
      // JSONでない場合はそのまま返す（ただし長すぎる場合は切り詰める）
      return body.length > 100 ? body.substring(0, 100) + '...' : body;
    }
  }

  // プリミティブ型の場合はそのまま返す
  if (typeof body !== 'object') return body;

  // 循環参照を追跡するためのWeakSetを初期化
  if (!visited) {
    visited = new WeakSet<object>();
  }

  // 既に訪問済みのオブジェクトの場合は循環参照として処理
  if (visited.has(body)) {
    return '[Circular Reference]';
  }

  // 現在のオブジェクトを訪問済みとしてマーク
  visited.add(body);

  // 配列の場合は各要素を再帰的に処理
  if (Array.isArray(body)) {
    return body.map((item) => sanitizeRequestBody(item, visited));
  }

  // オブジェクトの場合は各プロパティを処理
  const sanitized: Record<string, any> = {};
  // snake_case と camelCase の両方のパターンに対応
  const sensitivePatterns = [
    'password',
    'token',
    'refreshtoken',
    'refresh_token',
    'access_token',
    'accesstoken',
    'api_key',
    'apikey',
    'secret',
    'authorization',
    'auth_token',
    'authtoken'
  ];

  for (const [key, value] of Object.entries(body)) {
    const lowerKey = key.toLowerCase();

    // センシティブなフィールドをマスキング（_を除去して比較）
    const normalizedKey = lowerKey.replace(/_/g, '');
    if (sensitivePatterns.some((pattern) => normalizedKey.includes(pattern.replace(/_/g, '')))) {
      sanitized[key] = '[MASKED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeRequestBody(value, visited);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

// オブジェクトのキーを変換（内部ヘルパー関数）
const convertKeysToSnakeCaseInternal = (
  obj: ConvertibleValue,
  visited: WeakSet<object>
): unknown => {
  if (obj === null || obj === undefined) return obj;
  if (obj instanceof Date) return obj;
  if (typeof obj !== 'object') return obj;

  // 循環参照チェック
  if (visited.has(obj as object)) {
    return '[Circular Reference]';
  }
  visited.add(obj as object);

  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeysToSnakeCaseInternal(item, visited));
  }

  const converted: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = toSnakeCase(key);
    converted[snakeKey] = convertKeysToSnakeCaseInternal(value, visited);
  }
  return converted;
};

// オブジェクトのキーを変換（公開API）
export const convertKeysToSnakeCase = (obj: ConvertibleValue): unknown => {
  const visited = new WeakSet<object>();
  return convertKeysToSnakeCaseInternal(obj, visited);
};

// オブジェクトのキーを変換（内部ヘルパー関数）
const convertKeysToCamelCaseInternal = (
  obj: ConvertibleValue,
  visited: WeakSet<object>
): unknown => {
  if (obj === null || obj === undefined) return obj;
  if (obj instanceof Date) return obj;
  if (typeof obj !== 'object') return obj;

  // 循環参照チェック
  if (visited.has(obj as object)) {
    return '[Circular Reference]';
  }
  visited.add(obj as object);

  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeysToCamelCaseInternal(item, visited));
  }

  const converted: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = toCamelCase(key);
    converted[camelKey] = convertKeysToCamelCaseInternal(value, visited);
  }
  return converted;
};

// オブジェクトのキーを変換（公開API）
export const convertKeysToCamelCase = (obj: ConvertibleValue): unknown => {
  const visited = new WeakSet<object>();
  return convertKeysToCamelCaseInternal(obj, visited);
};

// Admin interceptor interface
interface AdminInterceptor {
  token: string;
  refreshFunc: () => Promise<boolean>;
  logoutFunc: () => void;
}

export class ApiClient {
  private config: Required<ApiConfig>;
  private abortControllers: Map<string, AbortController> = new Map();
  private adminToken: string | null = null;
  private adminInterceptors: AdminInterceptor[] = [];

  constructor(config: ApiConfig) {
    this.config = {
      baseURL: config.baseURL,
      timeout: config.timeout || 30000,
      retryCount: config.retryCount || 3,
      retryDelay: config.retryDelay || 1000
    };
  }

  // 認証トークンの取得
  private getAuthToken(): string | null {
    try {
      return localStorage.getItem('token');
    } catch (error) {
      logError(error as Error, 'ApiClient.getAuthToken');
      return null;
    }
  }

  // 現在の店舗IDの取得
  private getCurrentStoreId(): string | null {
    try {
      const storeData = localStorage.getItem('store');
      if (storeData) {
        const store = JSON.parse(storeData);
        return store.id ? String(store.id) : null;
      }
      return null;
    } catch (error) {
      logError(error as Error, 'ApiClient.getCurrentStoreId');
      return null;
    }
  }

  // リフレッシュトークンの取得
  private getRefreshToken(): string | null {
    try {
      return localStorage.getItem('refreshToken');
    } catch (error) {
      logError(error as Error, 'ApiClient.getRefreshToken');
      return null;
    }
  }

  // トークンの更新
  private async refreshAuthToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.config.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(convertKeysToSnakeCase({ refreshToken }))
      });

      if (response.ok) {
        const data = await response.json();

        // Validate response structure
        if (!data || typeof data !== 'object') {
          logError(new Error('Invalid response format'), 'ApiClient.refreshAuthToken', {
            response: 'Response is not an object'
          });
          return false;
        }

        // Check for required fields
        if (!data.access_token || typeof data.access_token !== 'string') {
          logError(new Error('Missing or invalid access_token'), 'ApiClient.refreshAuthToken', {
            hasAccessToken: !!data.access_token,
            accessTokenType: typeof data.access_token
          });
          return false;
        }

        if (!data.refresh_token || typeof data.refresh_token !== 'string') {
          logError(new Error('Missing or invalid refresh_token'), 'ApiClient.refreshAuthToken', {
            hasRefreshToken: !!data.refresh_token,
            refreshTokenType: typeof data.refresh_token
          });
          return false;
        }

        // Store tokens only after validation
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('refreshToken', data.refresh_token);

        // Store optional fields if they exist and are valid
        if (data.user && typeof data.user === 'object') {
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        if (data.store && typeof data.store === 'object') {
          localStorage.setItem('store', JSON.stringify(data.store));
        }

        return true;
      }
    } catch (error) {
      logError(error as Error, 'ApiClient.refreshAuthToken');
    }

    return false;
  }

  // リクエストのキャンセル
  public cancelRequest(requestId: string): void {
    // 完全一致でキャンセル
    const controller = this.abortControllers.get(requestId);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(requestId);
      return;
    }

    // ベースリクエストIDで始まるすべてのリクエストをキャンセル（リトライ含む）
    const keysToDelete: string[] = [];
    this.abortControllers.forEach((controller, key) => {
      if (key.startsWith(requestId)) {
        controller.abort();
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => this.abortControllers.delete(key));
  }

  // すべてのリクエストをキャンセル
  public cancelAllRequests(): void {
    this.abortControllers.forEach((controller) => controller.abort());
    this.abortControllers.clear();
  }

  // APIリクエストの実行
  public async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const url = `${this.config.baseURL}${endpoint}`;
    const method = options.method || 'GET';
    const startTime = performance.now();

    // リクエストボディの処理（リトライループの外で1回だけ実行）
    let requestBody: BodyInit | undefined = options.body as BodyInit | undefined;
    let isFormData = false;
    let isBlob = false;

    if (options.body && method !== 'GET') {
      // FormDataやBlobの場合はそのまま使用
      if (options.body instanceof FormData) {
        isFormData = true;
        requestBody = options.body;
      } else if (options.body instanceof Blob) {
        isBlob = true;
        requestBody = options.body;
      } else if (typeof options.body === 'string') {
        // 文字列の場合、JSONとして解析を試みる
        try {
          const bodyData = JSON.parse(options.body);
          const convertedData = convertKeysToSnakeCase(bodyData);
          requestBody = JSON.stringify(convertedData);
        } catch {
          requestBody = options.body;
        }
      }
    }

    // ヘッダーの構築（リトライループの外で1回だけ実行）
    const baseHeaders: Record<string, string> = {
      ...(options.headers as Record<string, string>)
    };

    // FormDataやBlobでない場合のみContent-Typeを設定
    if (!isFormData && !isBlob && !baseHeaders['Content-Type']) {
      baseHeaders['Content-Type'] = 'application/json';
    }

    // ログ記録（センシティブ情報をマスキング）
    const sanitizedBody = sanitizeRequestBody(requestBody);
    logApiRequest(method, endpoint, { body: sanitizedBody });
    const endMeasure = measureApiCall(endpoint);

    // リトライロジック
    const shouldRetry = options.retry !== false;
    const maxRetries = options.retryCount || this.config.retryCount;
    let lastError: Error | null = null;

    // 外部から指定されたrequestIdを使用（なければ生成）
    const baseRequestId = options.requestId || `${method}-${endpoint}-${Date.now()}`;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      // 各リトライごとに新しいAbortControllerを作成
      const requestId = `${baseRequestId}-attempt${attempt}`;
      const controller = new AbortController();
      this.abortControllers.set(requestId, controller);

      // 各リトライごとに新しいタイムアウトを設定
      const timeoutId = setTimeout(() => {
        controller.abort();
        this.abortControllers.delete(requestId);
      }, options.timeout || this.config.timeout);

      // 認証トークンの追加（リトライごとに最新のトークンを取得）
      const headers = { ...baseHeaders };
      const token = this.getAuthToken();
      if (token && endpoint !== '/auth/login') {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // 店舗IDヘッダーの追加（マルチテナント対応）
      const storeId = this.getCurrentStoreId();
      if (storeId && endpoint !== '/auth/login') {
        headers['X-Store-Id'] = storeId;
      }

      const config: RequestInit = {
        ...options,
        method,
        headers,
        body: requestBody,
        signal: controller.signal
      };
      try {
        const response = await fetch(url, config);
        const duration = Math.round(performance.now() - startTime);

        clearTimeout(timeoutId);
        this.abortControllers.delete(requestId);

        logApiResponse(method, endpoint, response.status, duration);

        // 401エラーの処理（管理者リクエストの場合はスキップ）
        if (
          response.status === 401 &&
          endpoint !== '/auth/login' &&
          endpoint !== '/auth/refresh' &&
          endpoint !== '/auth/logout' &&
          !(options as any).isAdminRequest
        ) {
          const tokenRefreshed = await this.refreshAuthToken();
          if (tokenRefreshed) {
            // 新しいトークンでリトライ
            const newToken = this.getAuthToken();
            if (newToken) {
              config.headers = {
                ...config.headers,
                Authorization: `Bearer ${newToken}`
              };
              const retryResponse = await fetch(url, config);
              await handleApiResponse(retryResponse, `API request failed: ${endpoint}`);
              const data = await retryResponse.json();
              endMeasure();
              return convertKeysToCamelCase(data) as T;
            }
          }
          // リフレッシュ失敗時はログアウトイベントを発火
          window.dispatchEvent(new Event('auth:logout'));
        }

        // リトライ可能なエラーの判定
        if (
          shouldRetry &&
          attempt < maxRetries &&
          RETRYABLE_STATUS_CODES.includes(response.status)
        ) {
          const delay = calculateBackoffDelay(attempt, this.config.retryDelay);
          logPerformance(
            `Retrying ${endpoint} after ${delay}ms (attempt ${attempt + 1}/${maxRetries})`,
            delay
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        // エラーハンドリング
        await handleApiResponse(response, `API request failed: ${endpoint}`);

        // 成功レスポンスの処理
        const data = await response.json();
        endMeasure();
        return convertKeysToCamelCase(data) as T;
      } catch (error) {
        clearTimeout(timeoutId);
        this.abortControllers.delete(requestId);

        if (error instanceof Error) {
          // アボートエラーの場合
          if (error.name === 'AbortError') {
            throw new Error(`Request aborted: ${endpoint}`);
          }
          lastError = error;
        }

        // 最後の試行でなければ続行
        if (shouldRetry && attempt < maxRetries) {
          const delay = calculateBackoffDelay(attempt, this.config.retryDelay);
          logPerformance(
            `Retrying ${endpoint} after error: ${delay}ms (attempt ${attempt + 1}/${maxRetries})`,
            delay
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        endMeasure();
        throw lastError || new Error('Unknown error occurred');
      }
    }

    throw lastError || new Error(`Failed after ${maxRetries} retries`);
  }

  // GET リクエスト
  public async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  // POST リクエスト
  public async post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    let body: BodyInit | undefined;

    if (data instanceof FormData || data instanceof Blob) {
      // FormDataやBlobの場合はそのまま使用
      body = data;
    } else if (data !== undefined) {
      // それ以外の場合はJSON文字列化
      body = JSON.stringify(data);
    }

    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body
    });
  }

  // PUT リクエスト
  public async put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    let body: BodyInit | undefined;

    if (data instanceof FormData || data instanceof Blob) {
      // FormDataやBlobの場合はそのまま使用
      body = data;
    } else if (data !== undefined) {
      // それ以外の場合はJSON文字列化
      body = JSON.stringify(data);
    }

    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body
    });
  }

  // DELETE リクエスト
  public async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  // 管理者トークン設定
  public setAdminToken(token: string): void {
    this.adminToken = token;
    if (process.env.NODE_ENV === 'development') {
      console.log('[ApiClient] Admin token set:', token ? `${token.substring(0, 10)}...` : 'null');
    }
  }

  // 管理者トークンクリア
  public clearAdminToken(): void {
    this.adminToken = null;
  }

  // 管理者認証インターセプター追加
  public addAdminAuthInterceptor(
    token: string,
    refreshFunc: () => Promise<boolean>,
    logoutFunc: () => void
  ): number {
    const interceptor: AdminInterceptor = { token, refreshFunc, logoutFunc };
    this.adminInterceptors.push(interceptor);
    return this.adminInterceptors.length - 1;
  }

  // 管理者認証インターセプター削除
  public removeAdminAuthInterceptor(index: number): void {
    if (index >= 0 && index < this.adminInterceptors.length) {
      this.adminInterceptors.splice(index, 1);
    }
  }

  // 管理者認証の処理
  private async handleAdminAuth(): Promise<boolean> {
    // 最新のインターセプターを使用
    if (this.adminInterceptors.length > 0) {
      const interceptor = this.adminInterceptors[this.adminInterceptors.length - 1];
      try {
        // トークンのリフレッシュを試行
        const refreshed = await interceptor.refreshFunc();
        if (refreshed) {
          // リフレッシュ成功時は新しいトークンが設定されているはず
          return true;
        } else {
          // リフレッシュ失敗時はログアウト
          interceptor.logoutFunc();
          return false;
        }
      } catch (error) {
        logError(error as Error, 'ApiClient.handleAdminAuth');
        interceptor.logoutFunc();
        return false;
      }
    }
    return false;
  }

  // 管理者トークンを使用したリクエスト
  public async adminRequest<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const adminOptions = { ...options };

    // デバッグログ
    if (process.env.NODE_ENV === 'development') {
      console.log('[ApiClient] adminRequest:', {
        endpoint,
        hasAdminToken: !!this.adminToken,
        adminToken: this.adminToken ? `${this.adminToken.substring(0, 10)}...` : 'null'
      });
    }

    // 既存のヘッダーに管理者トークンを追加
    if (this.adminToken) {
      adminOptions.headers = {
        ...adminOptions.headers,
        Authorization: `Bearer ${this.adminToken}`,
        'X-Admin-Request': 'true'
      };
    } else {
      console.warn('[ApiClient] adminRequest called without admin token!', { endpoint });
      // トークンがない場合は401エラーを投げる
      throw new Error('No admin token available');
    }

    try {
      // 管理者リクエストは特別なフラグを設定して、通常の401処理をスキップ
      return await this.request<T>(endpoint, { ...adminOptions, isAdminRequest: true } as any);
    } catch (error: any) {
      // 401エラーの場合、管理者認証を処理
      // handleApiResponseで生成されるエラーはstatusプロパティを持つ
      if (error.status === 401 && endpoint !== '/auth/admin/refresh') {
        const authHandled = await this.handleAdminAuth();
        if (authHandled && this.adminToken) {
          // 新しいトークンでリトライ
          adminOptions.headers = {
            ...adminOptions.headers,
            Authorization: `Bearer ${this.adminToken}`,
            'X-Admin-Request': 'true'
          };
          return this.request<T>(endpoint, { ...adminOptions, isAdminRequest: true } as any);
        }
      }
      throw error;
    }
  }
}

// シングルトンインスタンス
const apiClient = new ApiClient({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'
});

export default apiClient;
